import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import { Delete, Save, Cancel, X } from '@mui/icons-material';
import { Plus, Pencil } from 'lucide-react';
import { StyledContainer, StyledCard, StyledTableContainer, StyledTableRow, StyledTableCell, StyledTableHeadCell, softColors, activeStatusStyles } from "../../../styles/purchaseInvoices/pages/commonTableStyles";
import allocationDateRangesStyles from '../../../styles/purchaseInvoices/pages/allocationDateRangesStyles';
import useAllocationDateRanges from '../../../hooks/purchaseInvoices/useAllocationDateRanges';
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';

const AllocationDateRanges = () => {
    const { isDesktop } = useResponsive();
    const [editingRange, setEditingRange] = useState(null);
    const [newRange, setNewRange] = useState({
        value: '',
        startDate: '',
        endDate: ''
    });
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [validationError, setValidationError] = useState('');

    const {
        dateRanges,
        loading,
        error,
        updateRange,
        addRange,
        deleteRange,
        refreshRanges
    } = useAllocationDateRanges();

    useEffect(() => {
        refreshRanges();
    }, []);

    const handleEditClick = (range) => {
        setEditingRange({
            id: range.id,
            value: range.value,
            startDate: range.startDate,
            endDate: range.endDate
        });
    };

    const handleSaveEdit = async () => {
        if (!validateRange(editingRange)) return;

        const success = await updateRange(editingRange.id, {
            value: parseFloat(editingRange.value),
            startDate: editingRange.startDate,
            endDate: editingRange.endDate
        });

        if (success) {
            setEditingRange(null);
            setValidationError('');
        }
    };

    const handleCancelEdit = () => {
        setEditingRange(null);
        setValidationError('');
    };

    const handleAddNew = async () => {
        if (!validateRange(newRange)) return;

        const success = await addRange({
            value: parseFloat(newRange.value),
            startDate: newRange.startDate,
            endDate: newRange.endDate
        });

        if (success) {
            setNewRange({ value: '', startDate: '', endDate: '' });
            setShowAddDialog(false);
            setValidationError('');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('האם אתה בטוח שברצונך למחוק טווח תאריכים זה?')) {
            await deleteRange(id);
        }
    };

    const validateRange = (range) => {
        if (!range.value || !range.startDate || !range.endDate) {
            setValidationError('יש למלא את כל השדות');
            return false;
        }

        const value = parseFloat(range.value);
        if (isNaN(value) || value <= 0) {
            setValidationError('יש להזין סכום תקין');
            return false;
        }

        const startDate = new Date(range.startDate);
        const endDate = new Date(range.endDate);

        if (startDate >= endDate) {
            setValidationError('תאריך התחלה חייב להיות לפני תאריך הסיום');
            return false;
        }

        return true;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('he-IL');
    };

    if (loading) {
        return (
            <Box sx={allocationDateRangesStyles.loadingContainer}>
                <CircularProgress size={48} thickness={4} />
                <Typography variant="h6" color="textSecondary" sx={allocationDateRangesStyles.loadingText}>
                    טוען נתונים...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={allocationDateRangesStyles.errorBox}>
                <Typography variant="h6" color="error" sx={allocationDateRangesStyles.errorText}>
                    שגיאה בטעינת הנתונים: {error}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => window.location.reload()}
                    sx={allocationDateRangesStyles.retryButton}
                >
                    נסה שנית
                </Button>
            </Box>
        );
    }

    return (
        <StyledContainer>
            <StyledCard sx={allocationDateRangesStyles.cardWithShadow}>
                <Box sx={allocationDateRangesStyles.headerBox}>
                    <Typography variant="h5" component="h2" sx={allocationDateRangesStyles.headerTitle}>
                        ניהול טווחי תאריכים לבדיקת הקצאה
                    </Typography>
                    <Button
                        color="primary"
                        size="medium"
                        startIcon={<Plus size={20} strokeWidth={3} />}
                        onClick={() => setShowAddDialog(true)}
                        sx={allocationDateRangesStyles.addButtonResponsive}
                    >
                        הוספת טווח חדש
                    </Button>
                </Box>

                <CardContent>
                    <Alert
                        severity="info"
                        sx={allocationDateRangesStyles.infoAlert}
                    >
                        טווחי התאריכים מגדירים את הסכום המקסימלי המותר לחשבוניות ללא מספר הקצאה לפי תקופות שונות.
                    </Alert>

                    <StyledTableContainer sx={allocationDateRangesStyles.tableContainer}>
                        <Table dir="rtl">
                            <TableHead>
                                <TableRow>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>מזהה</StyledTableHeadCell>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>סכום מקסימלי</StyledTableHeadCell>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>תאריך התחלה</StyledTableHeadCell>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>תאריך סיום</StyledTableHeadCell>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>סטטוס</StyledTableHeadCell>
                                    <StyledTableHeadCell align="center" sx={allocationDateRangesStyles.tableHeader}>פעולות</StyledTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dateRanges.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Box sx={allocationDateRangesStyles.emptyStateTable}>
                                                <Typography variant="h6" sx={allocationDateRangesStyles.emptyTitle}>
                                                    אין טווחי תאריכים להצגה
                                                </Typography>
                                                <Typography variant="body2" sx={allocationDateRangesStyles.emptySubtitle}>
                                                    לחץ על "הוסף טווח חדש" כדי להוסיף טווח תאריכים
                                                </Typography>
                                            </Box>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    dateRanges.map((range, index) => (
                                        <StyledTableRow
                                            key={range.id}
                                            sx={allocationDateRangesStyles.tableRow}
                                        >
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {range.id}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {editingRange?.id === range.id ? (
                                                    <TextField
                                                        type="number"
                                                        value={editingRange.value}
                                                        onChange={(e) => setEditingRange({
                                                            ...editingRange,
                                                            value: e.target.value
                                                        })}
                                                        size="small"
                                                        sx={{ width: 120, ...allocationDateRangesStyles.editTextField }}
                                                    />
                                                ) : (
                                                    formatCurrency(range.value)
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {editingRange?.id === range.id ? (
                                                    <TextField
                                                        type="date"
                                                        value={editingRange.startDate}
                                                        onChange={(e) => setEditingRange({
                                                            ...editingRange,
                                                            startDate: e.target.value
                                                        })}
                                                        size="small"
                                                        sx={allocationDateRangesStyles.editTextField}
                                                    />
                                                ) : (
                                                    formatDate(range.startDate)
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {editingRange?.id === range.id ? (
                                                    <TextField
                                                        type="date"
                                                        value={editingRange.endDate}
                                                        onChange={(e) => setEditingRange({
                                                            ...editingRange,
                                                            endDate: e.target.value
                                                        })}
                                                        size="small"
                                                        sx={allocationDateRangesStyles.editTextField}
                                                    />
                                                ) : (
                                                    formatDate(range.endDate)
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {isCurrentlyActive(range) ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={activeStatusStyles.active}
                                                    >
                                                        כן
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={activeStatusStyles.inactive}
                                                    >
                                                        לא
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={allocationDateRangesStyles.tableCell}>
                                                {editingRange?.id === range.id ? (
                                                    <Box sx={allocationDateRangesStyles.actionButtons}>
                                                        <IconButton
                                                            onClick={handleSaveEdit}
                                                            size="small"
                                                            sx={allocationDateRangesStyles.saveButton}
                                                        >
                                                            <Save fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={handleCancelEdit}
                                                            size="small"
                                                            sx={allocationDateRangesStyles.cancelButton}
                                                        >
                                                            <Cancel fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={allocationDateRangesStyles.actionButtons}>
                                                        <IconButton
                                                            onClick={() => handleEditClick(range)}
                                                            size="small"
                                                            sx={allocationDateRangesStyles.editButton}
                                                        >
                                                            <Pencil size={18} color="#1976d2" />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleDelete(range.id)}
                                                            size="small"
                                                            sx={allocationDateRangesStyles.deleteButton}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>

                    <Box sx={allocationDateRangesStyles.footerBox}>
                        <Typography variant="body2" sx={allocationDateRangesStyles.counterText}>
                            {dateRanges.length > 0
                                ? `סה"כ ${dateRanges.length} טווחי תאריכים`
                                : ''
                            }
                        </Typography>
                    </Box>
                </CardContent>
            </StyledCard>

            {/* Add New Range Dialog */}
            <Dialog
                open={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: allocationDateRangesStyles.dialog }}
            >
                <DialogTitle sx={allocationDateRangesStyles.dialogTitle}>
                    <Typography sx={allocationDateRangesStyles.dialogTitleText}>
                        הוסף טווח תאריכים חדש
                    </Typography>

                    <IconButton
                        onClick={() => setShowAddDialog(false)}
                        sx={allocationDateRangesStyles.dialogCloseButton}
                        size="small"
                    >
                        <X size={20} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={allocationDateRangesStyles.dialogContent}>
                    <Box sx={allocationDateRangesStyles.formContainer}>
                        <TextField
                            label="סכום מקסימלי (ש״ח)"
                            type="number"
                            value={newRange.value}
                            onChange={(e) => setNewRange({ ...newRange, value: e.target.value })}
                            fullWidth
                            variant="outlined"
                            sx={allocationDateRangesStyles.formField}
                        />
                        <TextField
                            label="תאריך התחלה"
                            type="date"
                            value={newRange.startDate}
                            onChange={(e) => setNewRange({ ...newRange, startDate: e.target.value })}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={allocationDateRangesStyles.formField}
                        />
                        <TextField
                            label="תאריך סיום"
                            type="date"
                            value={newRange.endDate}
                            onChange={(e) => setNewRange({ ...newRange, endDate: e.target.value })}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={allocationDateRangesStyles.formField}
                        />
                        {validationError && (
                            <Alert severity="error" sx={allocationDateRangesStyles.errorAlert}>
                                {validationError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={allocationDateRangesStyles.dialogActions}>
                    <Button
                        onClick={() => setShowAddDialog(false)}
                        sx={allocationDateRangesStyles.dialogCancelButton}
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleAddNew}
                        variant="contained"
                        sx={allocationDateRangesStyles.dialogSubmitButton}
                    >
                        הוסף
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledContainer>
    );
};

const isCurrentlyActive = (range) => {
    const now = new Date();
    const startDate = new Date(range.startDate);
    const endDate = new Date(range.endDate);
    return now >= startDate && now <= endDate;
};

export default AllocationDateRanges;